import type {
  AccountId,
  ShopperAiTagGroupId,
} from "@redotech/effect-api-shared/ids/definitions";
import type { TagResponse } from "@redotech/effect-api-shared/rpc/tag";
import { PillTheme } from "@redotech/redo-model/pill-theme";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import type { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { PillColorPicker } from "@redotech/redo-web/tags/tag-input-color-picker";
import { TagPillThemes } from "@redotech/redo-web/tags/tag-pill";
import { Text } from "@redotech/redo-web/text";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { memo, useMemo, useState } from "react";
import { useCreateSupportTicketTag } from "../hooks/use-create-support-ticket-tag";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";

export interface CreateTagModalProps {
  /**
   * Account scope for the new tag. The available tag groups and the
   * duplicate-name check are filtered to this account. Required because
   * support-v2 tags must belong to a tag group that belongs to the account.
   */
  accountId?: AccountId;
  onClose: () => void;
  /** Called with the created tag so callers can apply it to a ticket. */
  onCreated?: (tag: TagResponse) => void;
}

export const CreateTagModal = memo(function CreateTagModal({
  accountId,
  onClose,
  onCreated,
}: CreateTagModalProps) {
  const collections = useGlobalSupportTicketCollections();
  const tagGroupsQuery = useLiveQuery(
    (q) =>
      accountId === undefined
        ? q.from({ tagGroup: collections.tagGroups })
        : q
            .from({ tagGroup: collections.tagGroups })
            .where(({ tagGroup }) => eq(tagGroup.accountId, accountId)),
    [collections.tagGroups, accountId],
  );
  const tagsQuery = useLiveQuery(
    (q) =>
      accountId === undefined
        ? q.from({ tag: collections.tags })
        : q
            .from({ tag: collections.tags })
            .where(({ tag }) => eq(tag.accountId, accountId)),
    [collections.tags, accountId],
  );
  const tagGroups = useMemo(
    () => tagGroupsQuery.data ?? [],
    [tagGroupsQuery.data],
  );
  const tags = useMemo(() => tagsQuery.data ?? [], [tagsQuery.data]);

  const [name, setName] = useState("");
  const [pillTheme, setPillTheme] = useState<PillTheme>(
    PillTheme.PRIMARY_LIGHT,
  );
  const [selectedGroupId, setSelectedGroupId] = useState<
    ShopperAiTagGroupId | undefined
  >(undefined);

  const createTag = useCreateSupportTicketTag();

  const groupOptions = useMemo<TextRedoListItem<ShopperAiTagGroupId>[]>(
    () =>
      tagGroups.map((group) => ({
        id: group.id,
        type: "text" as const,
        text: group.name,
        value: group.id,
      })),
    [tagGroups],
  );

  // Default to the first group when the user hasn't picked one, so the form is
  // submittable as soon as a name is entered. Derived rather than synced via an
  // effect.
  const effectiveGroupId = selectedGroupId ?? tagGroups[0]?.id;
  const selectedGroupOption = useMemo(
    () => groupOptions.find((option) => option.value === effectiveGroupId),
    [groupOptions, effectiveGroupId],
  );

  const trimmedName = name.trim();
  const nameConflict = useMemo(
    () =>
      tags.some(
        (tag) =>
          tag.tagGroupId === effectiveGroupId &&
          tag.name.toLowerCase() === trimmedName.toLowerCase(),
      ),
    [tags, effectiveGroupId, trimmedName],
  );

  const canCreate =
    trimmedName.length > 0 &&
    effectiveGroupId !== undefined &&
    !nameConflict &&
    !createTag.isPending;

  async function handleCreate() {
    if (!canCreate || effectiveGroupId === undefined) {
      return;
    }
    try {
      const tag = await createTag.mutateAsync({
        tagGroupId: effectiveGroupId,
        name: trimmedName,
        pillTheme,
      });
      onCreated?.(tag);
      onClose();
    } catch {
      // Error toasts are surfaced by the mutation hook / catchApiErrors.
    }
  }

  return (
    <RedoModal
      isOpen
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Create",
        onClick: handleCreate,
        disabled: !canCreate,
        pending: createTag.isPending,
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      title="Create tag"
    >
      <Flex dir="column" gap="3xl" p="sm">
        <Flex dir="column" gap="xs">
          <RedoTextInput label="Tag name" setValue={setName} value={name} />
          {nameConflict && (
            <Text fontSize="xs" pl="sm" textColor="error">
              {`A tag named "${trimmedName}" already exists in this group`}
            </Text>
          )}
        </Flex>
        <Flex dir="column" gap="xs">
          <RedoSingleSelectDropdownInput
            label="Tag group"
            options={groupOptions}
            optionSelected={({ item }) => setSelectedGroupId(item.value)}
            placeholder="Select a group"
            selectedItem={selectedGroupOption}
            size="sm"
          />
          {tagGroups.length === 0 && (
            <Text fontSize="xs" pl="sm" textColor="tertiary">
              Create a tag group in settings before adding tags.
            </Text>
          )}
        </Flex>
        <PillColorPicker
          label="Tag color"
          options={TagPillThemes}
          setValue={setPillTheme}
          value={pillTheme}
        />
      </Flex>
    </RedoModal>
  );
});
